/**
 * Blog Latest Articles Section – custom element
 * Single sticky nav uses links (blog.url / tagged). Filtering by URL (current_tags).
 * List: load more by 1 row (3 cards) per click.
 */
class BlogLatestArticlesSection extends HTMLElement {
  constructor() {
    super();
    this._listGrid = null;
    this._paginationEl = null;
    this._loadMoreBtn = null;
    this._isLoading = false;
    this._promoBanner = null;
    this._promoSticky = null;
    this._promoStickyInner = null;
    this._promoObserver = null;
    this._footerObserver = null;
    this._footerMain = null;
    this._isPromoVisible = true;
    this._isFooterVisible = false;
    this._isViewportAllowedForSticky = true;
    this._boundOnLoadMoreClick = this._onLoadMoreClick.bind(this);
    this._boundOnResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this._cacheRefs();
    this._initPromoSticky();
    this._attachListeners();
    window.addEventListener('resize', this._boundOnResize, { passive: true });
  }

  disconnectedCallback() {
    this._detachListeners();
    this._destroyPromoSticky();
    window.removeEventListener('resize', this._boundOnResize);
    this._listGrid = null;
    this._paginationEl = null;
    this._loadMoreBtn = null;
  }

  _cacheRefs() {
    this._listGrid = this.querySelector('.blog-latest-articles__list-grid');
    this._paginationEl = this.querySelector('.blog-latest-articles__pagination');
    this._loadMoreBtn = this.querySelector('.blog-latest-articles__load-more-btn');
    this._promoBanner = this.querySelector('.blog-latest-articles__promo-banner');
    this._promoSticky = this.querySelector('.blog-latest-articles__promo-sticky');
    this._promoStickyInner = this.querySelector('.blog-latest-articles__promo-sticky-inner');
    this._footerMain = document.querySelector('footer.footer__main');
  }

  _initPromoSticky() {
    if (this.dataset.enablePromoSticky !== 'true') return;
    if (!this._promoBanner || !this._promoSticky || !this._promoStickyInner) return;
    this._isViewportAllowedForSticky = this._isStickyAllowedForViewport();
    this._promoStickyInner.innerHTML = '';
    const promoClone = this._promoBanner.cloneNode(true);
    promoClone.setAttribute('aria-hidden', 'true');
    this._promoStickyInner.appendChild(promoClone);

    this._promoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this._promoBanner) return;
          this._isPromoVisible = entry.isIntersecting;
          this._updatePromoStickyVisibility();
        });
      },
      { threshold: 0.05 }
    );
    this._promoObserver.observe(this._promoBanner);
    this._initFooterObserver();
    this._updatePromoStickyVisibility();
  }

  _initFooterObserver() {
    if (this.dataset.hidePromoStickyOnFooter !== 'true') return;
    if (!this._footerMain) return;
    this._footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this._footerMain) return;
          this._isFooterVisible = entry.isIntersecting;
          this._updatePromoStickyVisibility();
        });
      },
      { threshold: 0.05 }
    );
    this._footerObserver.observe(this._footerMain);
  }

  _destroyPromoSticky() {
    if (this._promoObserver) {
      this._promoObserver.disconnect();
      this._promoObserver = null;
    }
    if (this._footerObserver) {
      this._footerObserver.disconnect();
      this._footerObserver = null;
    }
    if (this._promoSticky) {
      this._promoSticky.classList.remove('translate-y-0', 'opacity-100', 'pointer-events-auto');
      this._promoSticky.classList.add('translate-y-full', 'opacity-0', 'pointer-events-none');
    }
    if (this._promoStickyInner) {
      this._promoStickyInner.innerHTML = '';
    }
    this._isFooterVisible = false;
  }

  _onResize() {
    if (this.dataset.enablePromoSticky !== 'true' || !this._promoSticky) return;
    this._isViewportAllowedForSticky = this._isStickyAllowedForViewport();
    this._updatePromoStickyVisibility();
  }

  _isStickyAllowedForViewport() {
    const visibilityMode = this.dataset.promoStickyVisibility || 'mobile_only';
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (visibilityMode === 'mobile_desktop') return true;
    if (visibilityMode === 'desktop_only') return isDesktop;
    return !isDesktop;
  }

  _updatePromoStickyVisibility() {
    if (!this._promoSticky) return;
    const shouldShow = this._isViewportAllowedForSticky && !this._isPromoVisible && !this._isFooterVisible;
    this._promoSticky.classList.toggle('translate-y-full', !shouldShow);
    this._promoSticky.classList.toggle('opacity-0', !shouldShow);
    this._promoSticky.classList.toggle('pointer-events-none', !shouldShow);
    this._promoSticky.classList.toggle('translate-y-0', shouldShow);
    this._promoSticky.classList.toggle('opacity-100', shouldShow);
    this._promoSticky.classList.toggle('pointer-events-auto', shouldShow);
  }

  _attachListeners() {
    if (this._loadMoreBtn) {
      this._loadMoreBtn.addEventListener('click', this._boundOnLoadMoreClick);
    }
  }

  _detachListeners() {
    if (this._loadMoreBtn) {
      this._loadMoreBtn.removeEventListener('click', this._boundOnLoadMoreClick);
    }
  }

  _setLoadingState(isLoading) {
    if (!this._loadMoreBtn) return;
    this._isLoading = isLoading;
    this._loadMoreBtn.disabled = isLoading;
    const textSpan = this._loadMoreBtn.querySelector('span');
    const iconSpan = this._loadMoreBtn.querySelectorAll('span')[1];
    if (textSpan) {
      textSpan.textContent = isLoading ? 'LOADING...' : 'LOAD MORE ARTICLES';
    }
    if (iconSpan) {
      iconSpan.classList.toggle('hidden', isLoading);
    }
  }

  _replaceLoadMoreButton(nextUrl) {
    if (!this._paginationEl) return;
    if (!nextUrl) {
      this._paginationEl.innerHTML = '';
      this._loadMoreBtn = null;
      return;
    }
    this._paginationEl.innerHTML =
      '<button type="button" class="blog-latest-articles__load-more-btn btn-primary w-full max-sm:text-[18.73px] mb-[24px] max-w-[497px] lg:max-w-[420px] xl:max-w-[497px] lg:text-[21px] lg:leading-[21px] xl:text-[25px] xl:leading-[25px] lg:h-[60px] xl:h-[69px]" data-next-url="' +
      nextUrl +
      '">' +
      '<span>LOAD MORE ARTICLES</span>' +
      '<span>' +
      '<svg width="23" height="17" viewBox="0 0 23 17" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M2.08398 7.43318C1.45866 7.43318 0.951735 7.94011 0.951735 8.56543C0.951735 9.19075 1.45866 9.69768 2.08398 9.69768L2.08398 7.43318ZM22.2304 9.36605C22.6726 8.92388 22.6726 8.20698 22.2304 7.76481L15.0248 0.559215C14.5826 0.117045 13.8657 0.117045 13.4236 0.559216C12.9814 1.00139 12.9814 1.71829 13.4236 2.16046L19.8285 8.56543L13.4236 14.9704C12.9814 15.4126 12.9814 16.1295 13.4236 16.5716C13.8657 17.0138 14.5826 17.0138 15.0248 16.5716L22.2304 9.36605ZM2.08398 8.56543L2.08398 9.69768L21.4298 9.69768L21.4298 8.56543L21.4298 7.43318L2.08398 7.43318L2.08398 8.56543Z" fill="white"></path>' +
      '</svg>' +
      '</span>' +
      '</button>';
    this._loadMoreBtn = this._paginationEl.querySelector('.blog-latest-articles__load-more-btn');
    if (this._loadMoreBtn) {
      this._loadMoreBtn.addEventListener('click', this._boundOnLoadMoreClick);
    }
  }

  async _onLoadMoreClick(event) {
    const loadMoreBtn = event.currentTarget;
    if (!loadMoreBtn || this._isLoading || !this._listGrid) return;
    const nextUrl = loadMoreBtn.getAttribute('data-next-url');
    if (!nextUrl) return;

    this._setLoadingState(true);
    try {
      const response = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Failed to load next page');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextCards = doc.querySelectorAll('.blog-latest-articles__list-card');
      nextCards.forEach((card) => {
        this._listGrid.appendChild(card);
      });

      const nextButton = doc.querySelector('.blog-latest-articles__load-more-btn');
      const upcomingUrl = nextButton ? nextButton.getAttribute('data-next-url') : null;
      this._detachListeners();
      this._replaceLoadMoreButton(upcomingUrl);
    } catch (error) {
      this._setLoadingState(false);
      console.error('[blog-latest-articles] Load more failed', error);
      return;
    }

    this._setLoadingState(false);
  }
}

if (!customElements.get('blog-latest-articles-section')) {
  customElements.define('blog-latest-articles-section', BlogLatestArticlesSection);
}
